/**
 * Grund-Layout — Plattform-Shell wie die übrigen RMNC-Apps:
 * EINE dunkle Kopfleiste (Muster rmnc-personal TopBar.tsx, UX_KONZEPT §1.2)
 * mit Wortmarke, Navigation, AppSwitcher (9-Punkte-Grid), Benutzer, Abmelden.
 */

import { LogOut, Search } from "lucide-react"
import { NavLink, Outlet } from "react-router-dom"
import { AppSwitcher } from "@/components/AppSwitcher"
import { SuchPalette, suchPaletteOeffnen } from "@/components/SuchPalette"
import { performLogout, useAuthStore } from "@/store/auth"

const istMac = navigator.platform.toUpperCase().includes("MAC")

export function MainLayout() {
  const user = useAuthStore((s) => s.user)
  const istReviewer = useAuthStore((s) => s.isReviewer())

  return (
    <div className="min-h-screen">
      <div className="flex h-14 shrink-0 flex-nowrap items-center gap-3 bg-slate-900 px-4 text-white">
        {/* Wortmarke — nie gequetscht/abgeschnitten */}
        <span className="shrink-0 whitespace-nowrap text-sm font-semibold">
          ninox-<span style={{ color: "#4da3ff" }}>tester</span>
        </span>

        <nav className="flex min-w-0 gap-1 text-sm">
          {(
            [
              ["/", "Übersicht"],
              ["/melden", "Melden"],
              ["/handbuch", "Handbuch"],
              ...(istReviewer
                ? ([
                    ["/review", "Review"],
                    ["/admin/schema", "Schema"],
                    ["/admin/ki", "Administration"],
                  ] as const)
                : []),
            ] as const
          ).map(([pfad, label]) => (
            <NavLink
              key={pfad}
              to={pfad}
              end={pfad === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-1.5 transition-colors ${
                  isActive
                    ? "bg-slate-800 font-medium text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex flex-1 items-center justify-end gap-2">
          {/* Schnellsuche (Cmd/Ctrl-K) */}
          <button
            type="button"
            onClick={suchPaletteOeffnen}
            className="flex shrink-0 items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <Search className="h-4 w-4" />
            <span className="hidden lg:inline">Suchen</span>
            <kbd className="hidden rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-[11px] font-semibold text-slate-400 lg:inline">
              {istMac ? "⌘K" : "Strg K"}
            </kbd>
          </button>

          {/* App-Switcher (9-Punkte-Grid + Panel, 1:1 aus rmnc-personal) */}
          <AppSwitcher currentAppKey="tester" roles={user?.roles ?? []} />

          {/* Benutzername — einziges truncate-Element dieser Zeile */}
          <span className="hidden min-w-0 max-w-[10rem] truncate text-sm text-slate-300 md:inline">
            {user?.name ?? "Nicht angemeldet"}
          </span>

          {/* Abmelden */}
          <button
            type="button"
            onClick={() => void performLogout()}
            aria-label="Abmelden"
            className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden text-sm md:inline">Abmelden</span>
          </button>
        </div>
      </div>
      {/* 36/40px Innenabstand laut Design-Handoff; py-9 + h-14 = 8rem,
          darauf verlässt sich die Höhen-Rechnung der Erfassungsseite */}
      <main className="px-10 py-9">
        <Outlet />
      </main>
      <SuchPalette />
    </div>
  )
}
